import updateImmutable, { Context, CustomCommands, Spec } from 'immutability-helper';
import { postEditorErrorKeys, PostEditorErrorKeys } from '../../../../models/validator-props';
import { PostEditorErrors, PostEditorPostError, PostEditorPostErrors } from '../../../../models/post-editor';
import { cloneDeep, intersection, isArray, isEqual } from 'lodash';
import { ErrorMessageSeverity } from '../../../../constants/form';

declare type ExtendedSpec<T, C extends CustomCommands<object> = never> =
  | Spec<T, C>
  | {
      $errorMerge?: T;
    }
  | {
      [K in keyof T]?: ExtendedSpec<T[K], C>;
    };

export declare class ExtendedContext extends Context {
  // ... rest of the code, replacing ObjectSpec with ExtendedObjectSpec where needed
  update<T, C extends CustomCommands<object> = never>(object: T, $spec: ExtendedSpec<T, C>): T; // replace Spec with your own extended Spec type definition
}

const isKeyOfPostEditorErrors = (key: string): key is PostEditorErrorKeys =>
  postEditorErrorKeys.includes(key as PostEditorErrorKeys);

const isPostEditorPostErrorArrayGuard = (value: PostEditorPostErrors): value is PostEditorPostError[] => isArray(value);
const isPostEditorPostErrorGuard = (value: PostEditorPostErrors): value is PostEditorPostError =>
  !isArray(value) && typeof value === 'object';

const getDetailedType = (value: PostEditorPostErrors): 'array' | 'object' | 'boolean' => {
  if (isArray(value)) {
    return 'array';
  } else if (typeof value === 'object') {
    return 'object';
  } else {
    return 'boolean';
  }
};

const ImmutabilityContext: ExtendedContext = new Context();

ImmutabilityContext.extend<PostEditorErrors>('$errorMerge', (errors, original) => {
  // errors = source, original = target;
  const copy = cloneDeep(original);

  for (const key in errors) {
    if (isKeyOfPostEditorErrors(key)) {
      if (!copy.hasOwnProperty(key) || getDetailedType(copy[key]) !== getDetailedType(errors[key])) {
        copy[key] = cloneDeep(errors[key]);
      } else {
        const existingErrors = copy[key];
        const incomingErrors = errors[key];
        if (isPostEditorPostErrorArrayGuard(existingErrors) && isPostEditorPostErrorArrayGuard(incomingErrors)) {
          // Merge logic for arrays
          incomingErrors.forEach(eItem => {
            const oItemIndex = existingErrors.findIndex(oItem => {
              const incommingProfilesArray = oItem.selectedSocialProfiles
                .map(p => `${p.__typename}-${p.profileId}`)
                .sort();
              const existingProfilesArray = eItem.selectedSocialProfiles
                .map(p => `${p.__typename}-${p.profileId}`)
                .sort();
              // we are checking if incomming errors already exist in the existing errors, an if yes we just merge severity.
              // we also check if the slected profiles are equal because if we are adding new profiles in shared mode, severity should stay the same
              const intersectionresult = intersection(incommingProfilesArray, existingProfilesArray);
              return (
                oItem.variationId === eItem.variationId &&
                oItem.profileId === eItem.profileId &&
                incommingProfilesArray.length === intersectionresult.length
              );
            });

            // if we have the index it means that it already exists in existing, and we just need to merge severity
            // if nott, we are pushing the new error to the array
            if (oItemIndex !== -1) {
              // Merging severity
              existingErrors[oItemIndex].severity =
                eItem.severity === ErrorMessageSeverity.error ||
                existingErrors[oItemIndex].severity === ErrorMessageSeverity.warning
                  ? eItem.severity
                  : existingErrors[oItemIndex].severity;
              const incommingProfiles = eItem.selectedSocialProfiles
                .map(p => `${p.__typename}-${p.profileId}`)
                .sort()
                .join();
              const existingProfiles = existingErrors[oItemIndex].selectedSocialProfiles
                .map(p => `${p.__typename}-${p.profileId}`)
                .sort()
                .join();
              const sameSelectedProfiles = existingProfiles === incommingProfiles;
              if (!sameSelectedProfiles && eItem.selectedSocialProfiles.length) {
                existingErrors[oItemIndex].selectedSocialProfiles = eItem.selectedSocialProfiles;
              }
            } else {
              existingErrors.push(eItem);
            }
          });
          if (existingErrors.length > incomingErrors.length) {
            // some errors are fixed, so let's find them and remove
            const fixedEffors = existingErrors.filter(
              eItem =>
                !!incomingErrors.find(iItem => {
                  const existingProfiles = eItem.selectedSocialProfiles
                    .map(p => `${p.__typename}-${p.profileId}`)
                    .sort()
                    .join();
                  const incommingProfiles = iItem.selectedSocialProfiles
                    .map(p => `${p.__typename}-${p.profileId}`)
                    .sort()
                    .join();
                  const sameSelectedProfiles = existingProfiles === incommingProfiles;
                  return (
                    iItem.profileId === eItem.profileId &&
                    iItem.variationId === eItem.variationId &&
                    sameSelectedProfiles
                  );
                })
            );
            if (fixedEffors.length !== existingErrors.length) {
              copy[key] = fixedEffors;
            }
          }
        } else if (isPostEditorPostErrorGuard(existingErrors) && isPostEditorPostErrorGuard(incomingErrors)) {
          // Check if the selectedSocialProfiles are different before merging
          if (!isEqual(existingErrors.selectedSocialProfiles, incomingErrors.selectedSocialProfiles)) {
            // Merge the severity based on the incoming and existing severities
            copy[key] = updateImmutable(incomingErrors, {
              severity: {
                $apply: currentSeverity =>
                  currentSeverity === ErrorMessageSeverity.error ||
                  existingErrors.severity === ErrorMessageSeverity.warning
                    ? currentSeverity
                    : existingErrors.severity
              }
            });
          } else {
            // Merge the severity
            existingErrors.severity =
              incomingErrors.severity === ErrorMessageSeverity.error ||
              existingErrors.severity === ErrorMessageSeverity.warning
                ? incomingErrors.severity
                : existingErrors.severity;
          }
        } else {
          copy[key] = errors[key];
        }
      }
    }
  }

  return copy;
});

export { ImmutabilityContext };
